/**
 * SKU Repository
 */

import { apiClient } from '../../client'
import type { PaginatedResponse } from '../role.repository'
import { Client } from '../client.repository'
import { Principal } from './principal.repository'
import { Segment } from './segment.repository'
import { Sector } from './sector.repository'
import { Category } from './category.repository'
import { Subcategory } from './subcategory.repository'
import { Brand } from './brand.repository'
import { SkuGroup } from './sku-group.repository'
import { SKUDepartment } from './department.repository'
import { SKUBUSType } from './sku_bus.repository'
import { Tenant } from '../tenant.repository'


export interface Sku {
    id: number
    tenant_id: number
    client_id: number
    department_id: number
    ref_id_1?: string | null
    ref_id_2?: string | null
    en_name: string
    ar_name?: string | null
    sku_code?: string | null
    barcode?: string | null
    photo_name?: string
    front_image?: string
    back_image?: string
    principal_id: number
    segment_id: number
    sector_id: number
    category_id: number
    subcategory_id: number
    brand_id: number
    sku_group_id: number
    is_npd: number
    sku_class?: string | null
    value_segment?: string | null
    sku_tier?: string | null
    case_count: number
    outer_count: number
    pallet_count: number
    packaging_material?: string | null
    length_cm?: number | null
    width_cm?: number | null
    height_cm?: number | null
    net_weight?: number | null
    weight_unit?: string | null
    unit_price?: number | null
    case_price?: number | null
    outer_price?: number | null
    pallet_price?: number | null
    currency?: string | null
    dcp?: string | null
    shelf_life?: number | null
    temperature?: string | null
    unit_barcode?: number | null
    case_barcode?: number | null
    outer_barcode?: number | null
    pallet_barcode?: number | null
    meta?: any
    status: number
    created_by?: number | null
    updated_by?: number | null
    created_at: Date
    updated_at: Date
    client: Client
    principal: Principal
    segment: Segment
    sector: Sector
    category: Category
    subcategory: Subcategory
    brand: Brand
    skuGroup: SkuGroup
    department: SKUDepartment
    tenant: Tenant
    createdByUser?: {
        id: number,
        en_name: string
    }
    updatedByUser?: {
        id: number,
        en_name: string
    }
    skuBu: SKUBUSType
}

export interface CreateSkuRequest {
    tenant_id: number
    client_id: number
    department_id: number
    ref_id_1?: string
    ref_id_2?: string
    en_name: string
    ar_name?: string
    sku_code?: string
    barcode?: string
    photo_name?: string
    front_image?: string
    back_image?: string
    principal_id: number
    segment_id: number
    sector_id: number
    category_id: number
    subcategory_id: number
    brand_id: number
    sku_group_id: number
    is_npd?: number
    sku_class?: string
    value_segment?: string
    sku_tier?: string
    case_count: number
    outer_count: number
    pallet_count: number
    packaging_material?: string
    length_cm?: number
    width_cm?: number
    height_cm?: number
    net_weight?: number
    weight_unit?: string
    unit_price?: number
    case_price?: number
    outer_price?: number
    pallet_price?: number
    currency?: string
    dcp?: string | null
    shelf_life?: number | null
    temperature?: string | null
    unit_barcode?: number
    case_barcode?: number
    outer_barcode?: number
    pallet_barcode?: number
    meta?: any
    status?: number
    client?: Client
    principal?: Principal
    segment?: Segment
    sector?: Sector
    category?: Category
    subcategory?: Subcategory
    brand?: Brand
    skuGroup?: SkuGroup
}

export interface UpdateSkuRequest {
    tenant_id?: number
    client_id?: number
    department_id?: number
    ref_id_1?: string
    ref_id_2?: string
    en_name?: string
    ar_name?: string
    sku_code?: string
    barcode?: string
    photo_name?: string
    front_image?: string
    back_image?: string
    principal_id?: number
    segment_id?: number
    sector_id?: number
    category_id?: number
    subcategory_id?: number
    brand_id?: number
    sku_group_id?: number
    is_npd?: number
    sku_class?: string
    value_segment?: string
    sku_tier?: string
    case_count?: number
    outer_count?: number
    pallet_count?: number
    packaging_material?: string
    length_cm?: number
    width_cm?: number
    height_cm?: number
    net_weight?: number
    weight_unit?: string
    unit_price?: number
    case_price?: number
    outer_price?: number
    pallet_price?: number
    currency?: string
    dcp?: string | null
    shelf_life?: number | null
    temperature?: string | null
    unit_barcode?: number
    case_barcode?: number
    outer_barcode?: number
    pallet_barcode?: number
    meta?: any
    status?: number
    client?: Client
    principal?: Principal
    segment?: Segment
    sector?: Sector
    category?: Category
    subcategory?: Subcategory
    brand?: Brand
    skuGroup?: SkuGroup
}

export interface totalSkusByBU {
    bu_id: number;
    total_skus: number;
    bu_name: string;
}

class SkuRepository {
    async getAll(page: number = 1, limit: number = 25, filters?: any): Promise<PaginatedResponse<Sku>> {
        const params = new URLSearchParams({
            page: page.toString(),
            limit: limit.toString(),
        })

        if (filters) {
            for (const [key, value] of Object.entries(filters)) {
                if (value === undefined || value === null || value === '') continue;
                if (typeof value === 'object') {
                    params.set(key, JSON.stringify(value))
                } else {
                    params.set(key, String(value as string | number | boolean));

                }
            }
        }


        return apiClient.get<PaginatedResponse<Sku>>(`/skus?${params.toString()}`)
    }

    async getById(id: number): Promise<Sku> {
        return apiClient.get<Sku>(`/skus/${id}`)
    }

    async create(data: CreateSkuRequest): Promise<Sku> {
        return apiClient.post<Sku>('/skus', data)
    }

    async update(id: number, data: UpdateSkuRequest): Promise<Sku> {
        return apiClient.put<Sku>(`/skus/${id}`, data)
    }

    async delete(id: number): Promise<{ message: string }> {
        return apiClient.delete<{ message: string }>(`/skus/${id}`)
    }

    async exportToExcel(filters?: any): Promise<Blob> {
        const params = new URLSearchParams()
        if (filters) {
            for (const [key, value] of Object.entries(filters)) {
                if (value === undefined || value === null || value === '') continue;
                if (typeof value === 'object') {
                    params.set(key, JSON.stringify(value))
                } else {
                    params.set(key, String(value as string | number | boolean));

                }
            }
        }

        const baseUrl = `/skus/export/excel`;
        const queryString = params.toString();
        const url = queryString ? `${baseUrl}?${queryString}` : baseUrl;


        return apiClient.getBlob(url, true, { showSuccessToast: true, successMessage: 'Exported Successfully' })
    }

    async exportTemplateToExcel(filters?: any): Promise<Blob> {
        const params = new URLSearchParams()
        if (filters?.client_id) {
            params.append('client_id', filters.client_id.toString())
        }

        const baseUrl = `/skus/export/template`;
        const queryString = params.toString();
        const url = queryString ? `${baseUrl}?${queryString}` : baseUrl;

        return apiClient.getBlob(url, true, { showSuccessToast: true, successMessage: 'Exported Successfully' });
    }

    /**
     * Import SKUs from Excel file
     */
    async importFromExcel(file: File, clientId: number): Promise<{
        success: boolean;
        message: string;
        data: {
            success: boolean;
            total: number;
            created: number;
            updated: number;
            errors: Array<{ row: number; message: string }>;
        };
    }> {
        const formData = new FormData()
        formData.append('file', file)
        formData.append('client_id', clientId.toString())
        return apiClient.post('/skus/import/excel', formData)
    }

    async updateDcp(
        id: number,
        data: { dcp?: string | null; shelf_life?: number | null; temperature?: string | null },
    ): Promise<Sku> {
        return apiClient.put<Sku>(`/skus/dcp/${id}`, data)
    }

    async exportDcpToExcel(filters?: any): Promise<Blob> {
        const params = new URLSearchParams()
        if (filters) {
            for (const [key, value] of Object.entries(filters)) {
                if (value === undefined || value === null || value === '') continue
                params.set(key, String(value as string | number | boolean))
            }
        }
        const queryString = params.toString()
        const url = queryString ? `/skus/dcp/export/excel?${queryString}` : '/skus/dcp/export/excel'
        return apiClient.getBlob(url, true, { showSuccessToast: true, successMessage: 'Exported Successfully' })
    }

    async exportDcpTemplateToExcel(clientId: number): Promise<Blob> {
        const params = new URLSearchParams()
        params.append('client_id', clientId.toString())
        return apiClient.getBlob(`/skus/dcp/export/template?${params.toString()}`, true, {
            showSuccessToast: true,
            successMessage: 'Exported Successfully',
        })
    }

    async importDcpFromExcel(file: File, clientId: number): Promise<{
        success: boolean
        message: string
        data: {
            success: boolean
            total: number
            updated: number
            skipped: number
            errors: Array<{ row: number; message: string }>
        }
    }> {
        const formData = new FormData()
        formData.append('file', file)
        formData.append('client_id', clientId.toString())
        return apiClient.post('/skus/dcp/import/excel', formData)
    }

    // Analytics Methods
    async getFoodNonFoodAndActiveSkus() {
        return apiClient.get<{ food_skus: number; non_food_skus: number; active_skus: number; inactive_skus: number }>(
            '/skus/analytics/food-nonfood-active'
        )
    }

    async getDepartmentBreakdown() {
        return apiClient.get<Array<{ department: string; total_skus: number; percentage: number }>>(
            '/skus/analytics/departments'
        )
    }

    async getCategoryBreakdown() {
        return apiClient.get<Array<{ category: string; total_skus: number; percentage: number }>>(
            '/skus/analytics/categories'
        )
    }

    async getSubcategoryBreakdown() {
        return apiClient.get<Array<{ subcategory: string; total_skus: number; percentage: number }>>(
            '/skus/analytics/subcategories'
        )
    }

    async getBrandBreakdown() {
        return apiClient.get<Array<{ brand: string; total_skus: number; percentage: number }>>(
            '/skus/analytics/brands'
        )
    }

    async getSegmentBreakdown() {
        return apiClient.get<Array<{ sku_segment: string; total_skus: number; percentage: number }>>(
            '/skus/analytics/segments'
        )
    }

    async getSectorBreakdown() {
        return apiClient.get<Array<{ sku_sector: string; total_skus: number; percentage: number }>>(
            '/skus/analytics/sectors'
        )
    }

    async getSkuGroupBreakdown() {
        return apiClient.get<Array<{ sku_group: string; total_skus: number; percentage: number }>>(
            '/skus/analytics/sku-groups'
        )
    }
    async getPrincipals() {
        return apiClient.get<Array<{
            principal: string;
            total_skus: number;
            contribution_pct: number;
            num_departments: number;
            num_categories: number;
            num_subcategories: number;
            num_brands: number;
            num_segments: number;
            num_sectors: number;
            num_sku_groups: number;
        }>>(
            '/skus/analytics/principals'
        )
    }

    async getTotalSkuByBU(): Promise<totalSkusByBU[]> {
        return await apiClient.get<totalSkusByBU[]>('/skus/analytics/totalskuGroupByBU')

    }
}

export const skuRepository = new SkuRepository()

